import { useMemo, useRef, useState, useEffect } from 'react';
import styled from 'styled-components';

import SortableTable from '../Utils/SortableTable';
import { formatCPF } from '../../Funcoes/FormatCPF';
import { formatarData } from '../../Funcoes/FormatarData';
import { formatTelefone } from '../../Funcoes/FormatTelefone';

const PAGE_SIZE = 100;
const SCROLL_THRESHOLD = 200;

const TableShell = styled.div`
    width: 100%;
    flex: 1;
    background-color: #ffffff;
    border-radius: 15px;
    overflow: hidden;
`;

const ATRAS_COLOR = 'rgb(255, 123, 0)';

const formatSalario = (valor) => {
    if (valor == null || valor === '') return null;

    const raw = String(valor).trim();
    if (raw === '') return null;

    const clean = raw.replace(/\s/g, '');
    let parsed;

    // Ex.: "1.234,56" ou "16,01" (formato BR com vírgula decimal)
    if (/,(\d{1,2})$/.test(clean)) {
        parsed = Number(clean.replace(/\./g, '').replace(',', '.'));
    // Ex.: "1234.56" ou "16.01" (formato com ponto decimal)
    } else if (/\.(\d{1,2})$/.test(clean)) {
        parsed = Number(clean.replace(/,/g, ''));
    // Sem casas decimais explícitas: tenta como inteiro
    } else {
        parsed = Number(clean.replace(/[^\d-]/g, ''));
    }

    if (Number.isNaN(parsed)) {
        // Caso inesperado: mantém exatamente como veio do backend.
        return `R$ ${raw}`;
    }

    return parsed.toLocaleString('pt-BR', {
        style: 'currency',
        currency: 'BRL',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    });
};

const AdmissaoSquare = ({ tabelaValues, openToEdit, isLoading }) => {

    const tableRef = useRef(null);
    const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);

    /* Lazy loading: incrementa o limite de linhas conforme o usuário rola até o fim. */
    useEffect(() => {

        const el = tableRef.current;
        if (!el) return;

        const handleScroll = () => {

            const { scrollTop, scrollHeight, clientHeight } = el;
            if (scrollTop + clientHeight >= scrollHeight - SCROLL_THRESHOLD) {
                setVisibleCount((prev) => prev + PAGE_SIZE);
            }

        };

        el.addEventListener('scroll', handleScroll, { passive: true });
        return () => el.removeEventListener('scroll', handleScroll);

    }, []);

    /* Quando a base muda (filtros), volta para o topo e reinicia o chunk. */
    useEffect(() => {

        setVisibleCount(PAGE_SIZE);
        if (tableRef.current) tableRef.current.scrollTop = 0;

    }, [tabelaValues]);

    const columns = useMemo(() => [
        {
            id: 'cc_origem',
            header: 'ORIGEM CC',
            accessor: (r) => r.cc_origem,
            width: '140px',
        },
        {
            id: 'cc_destino',
            header: 'DESTINO CC',
            accessor: (r) => r.cc_destino,
            width: '140px',
        },
        {
            id: 'nome',
            header: 'NOME',
            accessor: (r) => r.nome,
            width: '260px',
        },
        {
            id: 'cpf',
            header: 'CPF',
            accessor: (r) => r.cpf,
            sortValue: (r) => r.cpf ?? '',
            render: (v) => (v ? formatCPF(v) : null),
            width: '140px',
        },
        {
            id: 'telefone',
            header: 'TELEFONE',
            accessor: (r) => r.telefone,
            sortValue: (r) => r.telefone ?? '',
            render: (v) => (v ? formatTelefone(v) : null),
            width: '150px',
        },
        {
            id: 'funcao',
            header: 'FUNÇÃO',
            accessor: (r) => r.funcao,
            width: '200px',
        },
        {
            id: 'salario',
            header: 'SALÁRIO',
            accessor: (r) => r.salario,
            sortValue: (r) => Number(r.salario) || 0,
            render: (v) => formatSalario(v),
            align: 'right',
            width: '120px',
        },
        {
            id: 'status',
            header: 'STATUS',
            accessor: (r) => r.status,
            width: '180px',
        },
        {
            id: 'data',
            header: 'DATA',
            accessor: (r) => r.data,
            sortValue: (r) => r.data ?? '',
            render: (v) => (v ? formatarData(v) : null),
            width: '110px',
        },
        {
            id: 'motivo',
            header: 'MOTIVO',
            accessor: (r) => r.motivo,
            width: '140px',
        },
        {
            id: 'desistente',
            header: 'DESISTÊNCIA',
            accessor: (r) => r.desistente,
            sortValue: (r) => (r.desistente === '*' ? 1 : 0),
            render: (v) => (v === '*' ? 'SIM' : null),
            align: 'center',
            width: '120px',
        },
    ], []);

    return (
        <TableShell>
            <SortableTable
                ref={tableRef}
                columns={columns}
                data={tabelaValues}
                limit={visibleCount}
                isLoading={isLoading}
                loadingText="Carregando processos..."
                emptyText="NENHUM PROCESSO ENCONTRADO"
                initialSort={{ key: 'data', direction: 'desc' }}
                onRowClick={openToEdit}
                rowCursor="pointer"
                getRowId={(row, idx) => row.identifier ?? `${row.motivo}-${row.id}-${idx}`}
                getRowProps={(row) => (
                    row.ordem_item === 1
                        ? { style: { color: ATRAS_COLOR, fontWeight: 'bold' } }
                        : null
                )}
            />
        </TableShell>
    );

};

export default AdmissaoSquare;
